import { call, put, takeLatest, select, take, takeLeading } from "redux-saga/effects";
import moment from "moment";
import ConsoleStore from 'ConsoleStore';

import {
  AI_FUTURE_EVENT_LABELS,
  AI_FUTURE_EVENT_TYPES,
  AINewsConstants,
  AINewsType,
  TREND,
} from "../../../Constants/AINewsConstants";
import GraphType from "../../../Constants/GraphType";
import aiNewsApi from "../../../ServiceApi/Apis/AINewsApi";
import {
  endDate,
  getAINewsStates,
  getDatagraphStates,
  getTimeLine,
  symbolSelect,
} from "../../../Reducers/NavDataGraph/TabDataGraph/selectors";
import periodicityHelper from "../../../Utils/PeriodicityHelper";
import GetZigzagTrendDataRequest from "../../../ServiceApi/Requests/GetZigzagTrendDataRequest";
import { eventChannel } from "redux-saga";
import baseServiceApi from "../../../ServiceApi/BaseServiceApi";
import GetCompetitiveEdgeRequest from "../../../ServiceApi/Requests/GetCompetitiveEdgeRequest";
import GetFinancialBlockMetricsDataRequest from "../../../ServiceApi/Requests/GetFinancialBlockMetricsDataRequest";
import { PriceChartConstants } from "../../../Constants/PriceChartConstants";
import { getRiPanelAIState } from "../../../Reducers/NavDataGraph/RelatedInfoPanel/selectors";
import { TabDataGraphConstants } from "../../../Constants/TabDataGraphConstants";
import { SettingsConstants } from "../../../Constants/SettingsConstants";
import UserInfoUtil from 'UserInfoUtil';

const { ActionTypes } = AINewsConstants;
const { ActionTypes: PriceChartActionTypes } = PriceChartConstants;
const EntitlementType = baseServiceApi.rayData["EntitlementType"];

function* onCompetitiveEdgeData(data) {
  const { competitiveEdgeData } = yield select(getAINewsStates);
  yield put({
    type: ActionTypes.GET_COMPETITIVE_EDGE_SUCCESS,
    data: competitiveEdgeData + data,
    message: "",
  });
}

function* onCompetitiveEdgeDataFailure() {
  yield put({
    type: ActionTypes.GET_COMPETITIVE_EDGE_FAILURE,
    data: "",
    message:
      "Something went wrong while fetching the data. Please try again after some time.",
  });
}

function* getCompetitiveEdgeData({ symbol }) {
  try {
    const { SymbolInfo, majorPeriodicity } = yield select(getDatagraphStates);
    const osid = SymbolInfo.Osid;
    const companyName = SymbolInfo.CompanyName;
    // closing previously streamed api
    yield call(closeCurrentEventSource);

    const request = new GetCompetitiveEdgeRequest(
      symbol,
      osid,
      companyName
    );

    ConsoleStore.trackUsage("CompetitiveEdgeClicked", "", ["AI", "Competitive Edge", "SummaryBlock", SymbolInfo.Symbol, majorPeriodicity]);
    yield call(handleStreamRequest, { request, onMessage: onCompetitiveEdgeData, onFailure: onCompetitiveEdgeDataFailure });
  } catch (error) {
    yield put({
      type: ActionTypes.GET_COMPETITIVE_EDGE_FAILURE,
      data: "",
      message:
        "Something went wrong while fetching the data. Please try again after some time.",
    });

    console.log(
      `%cError occurs in AINewsSaga.js, getCompetitiveEdgeData ${error}`,
      "color:#508F4E;background-color:#ffff00"
    );
  }
}

const calQtrs = [3, 6, 9, 12];
const f1Qtrs = [1, 4, 7, 10];
const f2Qtrs = [2, 5, 8, 11];

function getQuarter(date, fiscalMonthEnd) {
  let month = moment(date).month() + 1;
  const isF1 = f1Qtrs.includes(fiscalMonthEnd);
  const isF2 = f2Qtrs.includes(fiscalMonthEnd);
  const isCalMonth = calQtrs.includes(month);

  if (isF1 && isCalMonth) {
    const index = calQtrs.indexOf(month);
    month = f1Qtrs[index];
  } else if (isF2 && isCalMonth) {
    const index = calQtrs.indexOf(month);
    month = f2Qtrs[index];
  }

  const fiscalStartMonth = (fiscalMonthEnd % 12) + 1;
  const shiftedMonth = ((month - fiscalStartMonth + 12) % 12) + 1;
  return { fiscalMonth: month, fiscalQtr: Math.ceil(shiftedMonth / 3) };
}

function getFiscalYear(date, month, fiscalMonthEnd) {
  const year = moment(date).year();

  const fiscalStartMonth = (fiscalMonthEnd % 12) + 1;
  // Not incrementing the year if the fiscal year is same as calendar year.
  // This applies to stocks whose fiscal month end is December (fiscalMonthEnd === 12).
  // Example: check stocks like BRKA, AAL
  return month >= fiscalStartMonth && fiscalMonthEnd !== 12 ? year + 1 : year;
}

function* onFinancialBlockMetricsData(data) {
  const { financialBlockMetricsData } = yield select(getAINewsStates);
  yield put({
    type: ActionTypes.GET_FINANCIAL_BLOCK_METRICS_DATA_SUCCESS,
    data: financialBlockMetricsData + data,
    message: "",
  });
}

function* onfinancialBlockMetricsDataFailure() {
  yield put({
    type: ActionTypes.GET_FINANCIAL_BLOCK_METRICS_DATA_FAILURE,
    data: "",
    message:
      "Something went wrong while fetching the data. Please try again after some time.",
  });
}

function* getFinancialBlockMetricsData({ metrics, date }) {
  try {
    const symbol = yield select(symbolSelect);
    const { periodicity, SymbolInfo } = yield select(getDatagraphStates);
    const trackUsageProps = ["AI", "Financial Block AI Metrics", "Financial Block", SymbolInfo.Symbol, periodicity]
    let payload = { symbol, metrics };
    switch (periodicity) {
      case GraphType.Daily:
      case GraphType.Weekly:
        const { fiscalMonth, fiscalQtr } = getQuarter(
          date,
          SymbolInfo.FiscalMonthEnd
        );
        payload.quarter = fiscalQtr;
        payload.year = getFiscalYear(
          date,
          fiscalMonth,
          SymbolInfo.FiscalMonthEnd
        );
        trackUsageProps.push(`q${fiscalQtr}`, `${payload.year}`)
        break;
      case GraphType.Monthly:
        payload.year = moment(date).format("YYYY");
        trackUsageProps.push(`${payload.year}`)
        break;
      default:
        break;
    }
    // closing previously streamed api
    yield call(closeCurrentEventSource);

    const request = new GetFinancialBlockMetricsDataRequest(payload);
    ConsoleStore.trackUsage("FinancialMetricCellClicked", metrics, trackUsageProps);
    yield call(handleStreamRequest, { request, onMessage: onFinancialBlockMetricsData, onFailure: onfinancialBlockMetricsDataFailure });
  } catch (error) {
    yield put({
      type: ActionTypes.GET_FINANCIAL_BLOCK_METRICS_DATA_FAILURE,
      data: "",
      message:
        "Something went wrong while fetching the data. Please try again after some time.",
    });

    console.log(
      `%cError occurs in AINewsSaga.js, getFinancialBlockMetricsData ${error}`,
      "color:#508F4E;background-color:#ffff00"
    );
  }
}

function* onZigzagTrendData(data) {
  const { zigzagTrendData } = yield select(getAINewsStates);
  yield put({
    type: ActionTypes.GET_ZIGZAG_TREND_DATA_SUCCESS,
    data: zigzagTrendData + data,
    message: "",
  });
}

function* onZigzagTrendDataFailure() {
  yield put({
    type: ActionTypes.GET_ZIGZAG_TREND_DATA_FAILURE,
    data: "",
    message:
      "Something went wrong while fetching the data. Please try again after some time.",
  });
}

function* getZigzagTrendData({ startDate, endDate, trendType }) {
  try {
    const symbol = yield select(symbolSelect);
    const { SymbolInfo, majorPeriodicity } = yield select(getDatagraphStates);
    let { aiSettings } = yield select(getRiPanelAIState);
    const { swingSize } = aiSettings?.zigzagIndicator;
    const osid = SymbolInfo.Osid;
    const companyName = SymbolInfo.CompanyName;
    const formattedstartDate = moment(startDate).format("YYYY-MM-DD");
    const formattedendDate = moment(endDate).format("YYYY-MM-DD");
    // closing previously streamed api
    yield call(closeCurrentEventSource);

    const request = new GetZigzagTrendDataRequest(
      symbol,
      formattedstartDate,
      formattedendDate,
      trendType,
      osid,
      companyName
    );
    ConsoleStore.trackUsage("TrendLineClicked", `${moment(startDate).format('YYYY/MM/DD')}-${moment(endDate).format('YYYY/MM/DD')}`, ["AI", "Zigzag Indicator", "PriceChart", SymbolInfo.Symbol, majorPeriodicity, `${swingSize[majorPeriodicity]}`]);
    yield call(handleStreamRequest, { request, onMessage: onZigzagTrendData, onFailure: onZigzagTrendDataFailure });
  } catch (error) {
    yield put({
      type: ActionTypes.GET_ZIGZAG_TREND_DATA_FAILURE,
      data: "",
      message:
        "Something went wrong while fetching the data. Please try again after some time.",
    });

    console.log(
      `%cError occures in AINewsSaga.js, getCompetitiveEdgeData ${error}`
    );
  }
}

function* onTrackPriceAIData(data) {
  const { trackPriceAINews } = yield select(getAINewsStates);
  yield put({
    type: ActionTypes.GET_TRACK_PRICE_AI_NEWS_SUCCESS,
    data: trackPriceAINews + data,
    message: "",
  });
}

function* onTrackPriceAIDataFailure() {
  yield put({
    type: ActionTypes.GET_TRACK_PRICE_AI_NEWS_FAILURE,
    data: "",
    message:
      "Something went wrong while fetching the data. Please try again after some time.",
  });
};

function getTrackPriceStartDate(endDate, periodicity) {
  let startDate
  if (periodicity === GraphType.Daily) {
    startDate = endDate;
  }
  if (periodicity === GraphType.Weekly) {
    startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 4);
  }
  if (periodicity === GraphType.Monthly) {
    const year = endDate.getFullYear();
    const month = endDate.getMonth();

    startDate = new Date(year, month, 1); // 1st of the month

    // If it's Saturday (6) or Sunday (0), move to Monday
    const day = startDate.getDay();
    if (day === 0) {       // Sunday
      startDate.setDate(startDate.getDate() + 1);
    } else if (day === 6) { // Saturday
      startDate.setDate(startDate.getDate() + 2);
    }
  }
  return startDate;
}

function* getTrackPriceAINews({ endDate, trendType }) {
  try {
    const symbol = yield select(symbolSelect);
    const { SymbolInfo, majorPeriodicity } = yield select(getDatagraphStates)

    const osid = SymbolInfo.Osid;
    const companyName = SymbolInfo.CompanyName

    const startDate = getTrackPriceStartDate(endDate, majorPeriodicity);
    const formattedstartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedendDate = moment(endDate).format('YYYY-MM-DD');
    // closing previously streamed api
    yield call(closeCurrentEventSource);
    ConsoleStore.trackUsage("TrackPriceAIInvoked", `${moment(startDate).format('YYYY/MM/DD')}-${moment(endDate).format('YYYY/MM/DD')}`, ["AI", "Track Price AI News", "PriceChart", SymbolInfo.Symbol, majorPeriodicity]);

    const request = new GetZigzagTrendDataRequest(
      symbol,
      formattedstartDate,
      formattedendDate,
      trendType,
      osid,
      companyName
    );
    yield call(handleStreamRequest, { request, onMessage: onTrackPriceAIData, onFailure: onTrackPriceAIDataFailure });
  } catch (error) {
    yield put({
      type: ActionTypes.GET_TRACK_PRICE_AI_NEWS_FAILURE,
      data: '',
      message: 'Something went wrong while fetching the data. Please try again after some time.'
    });

    console.log(`%cError occures in AINewsSaga.js, getCompetitiveEdgeData ${error}`);
  }
}

function createStreamChannel(request) {
  return eventChannel((emitter) => {
    const eventSource = baseServiceApi.processEventSourceServiceCall(
      request,
      (message) => {
        if (message.final_answer) {
          emitter({ type: "message", data: message.final_answer });
        }
        else if (message.event_name === "COMPLETED") {
          emitter({ type: "completed" });
        } else if (message.event_name === "ERROR") {
          emitter({ type: "error", error: message.message });
        }
      }
    );
    eventSource
      .then(() => {
        emitter({ type: "end" });
      })
      .catch((error) => {
        emitter({ type: "error", error });
      });
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  });
}

function* handleStreamRequest({ request, onMessage, onFailure }) {
  try {
    const channel = yield call(createStreamChannel, request);
    try {
      let buffer = '';
      const MIN_LENGTH = 50;
      while (true) {
        const event = yield take(channel);
        if (event.type === "message") {
          buffer += event.data;
          if (buffer.length >= MIN_LENGTH) {
            yield call(onMessage, buffer);
            buffer = '';
          }
        } else if (event.type === "completed") {
          if (buffer.length > 0) {
            yield call(onMessage, buffer);
          }
          yield call(onEnd);
          break;
        }
        else if (event.type === "end") {
          break;
        } else if (event.type === "error") {
          yield call(onFailure);
          break;
        }
      }
    } catch (error) {
      console.log(
        `%cError occures in AINewsSaga.js, stream handler ${error}`
      );
    }
  } catch (error) {
    console.log(`%cError occures in AINewsSaga.js, stream handler ${error}`);
  }
}

function* setAITextReactions({ newsType, selectedSegment, financialDetails, trackPriceDate }) {
  try {
    const { periodicity, SymbolInfo } = yield select(getDatagraphStates);
    const { savedReactions } = yield select(getAINewsStates);
    if (Object.keys(savedReactions).length === 0) {
      return;
    }
    let trackUsageValue = "";
    const trackUsageProps = ["AI"]
    let payload = {
      newsType,
      OSID: SymbolInfo.Osid,
      periodicity: periodicityHelper.convertToPeriodicity(periodicity),
    };
    switch (newsType) {
      case AINewsType.TREND_NEWS: {
        let { aiSettings } = yield select(getRiPanelAIState);
        const { swingSize } = aiSettings?.zigzagIndicator;

        const formattedstartDate = moment(selectedSegment.startDate).format(
          "YYYY-MM-DD"
        );
        const formattedendDate = moment(selectedSegment.endDate).format(
          "YYYY-MM-DD"
        );
        payload.startDate = formattedstartDate;
        payload.endDate = formattedendDate;
        payload.trend = selectedSegment.upTrend
          ? TREND.UP_TREND
          : TREND.DOWN_TREND;
        const feedbackReactions = Object.values(savedReactions).map(
          (reaction) => {
            return {
              feedback_type: reaction.reaction,
              comments: reaction.comments,
              feedback_description: reaction.text,
              news_date: reaction.date
                ? moment(reaction.date).format("YYYY-MM-DD")
                : "",
            };
          }
        );
        payload.reactions = feedbackReactions;
        trackUsageValue = `${moment(selectedSegment.startDate).format('YYYY/MM/DD')}-${moment(selectedSegment.endDate).format('YYYY/MM/DD')}`;
        trackUsageProps.push("Zigzag Indicator", "PriceChart", SymbolInfo.Symbol, periodicity, `${swingSize[periodicity]}`)
        break;
      }
      case AINewsType.COMPETITIVE_NEWS: {
        payload.startDate = null;
        payload.endDate = null;
        payload.trend = null;
        const feedbackReactions = Object.values(savedReactions).map(
          (reaction) => {
            return {
              feedback_type: reaction.reaction,
              comments: reaction.comments,
              feedback_description: reaction.text,
              news_date: null,
            };
          }
        );
        payload.reactions = feedbackReactions;
        trackUsageProps.push("Competitive Edge", "Summary Block", SymbolInfo.Symbol, periodicity)
        break;
      }
      case AINewsType.FINANCIAL_BLOCK_METRICS: {
        payload.startDate = null;
        payload.endDate = null;
        payload.trend = null;
        const { fiscalMonth, fiscalQtr } = getQuarter(
          financialDetails.date,
          SymbolInfo.FiscalMonthEnd
        );
        payload.fiscal_quarter = fiscalQtr;
        payload.fiscal_year = getFiscalYear(
          financialDetails.date,
          fiscalMonth,
          SymbolInfo.FiscalMonthEnd
        );
        payload.financial_metric = financialDetails.metrics;
        const feedbackReactions = Object.values(savedReactions).map(
          (reaction) => ({
            feedback_type: reaction.reaction,
            comments: reaction.comments,
            feedback_description: reaction.text,
            news_date: null,
          })
        );
        payload.reactions = feedbackReactions;
        trackUsageValue = `${financialDetails.metrics}`;
        trackUsageProps.push("Financial Block AI Metrics", "Financial Block", SymbolInfo.Symbol, periodicity, `q${payload.fiscal_quarter}`, `${payload.fiscal_year}`)
        break;
      }
      case AINewsType.TRACK_PRICE_AI_NEWS: {
        const startDate = getTrackPriceStartDate(trackPriceDate, periodicity);
        payload.trackprice_date = moment(trackPriceDate).format("YYYY-MM-DD");
        const feedbackReactions = Object.values(savedReactions).map(
          (reaction) => ({
            feedback_type: reaction.reaction,
            comments: reaction.comments,
            feedback_description: reaction.text,
            news_date: reaction.date
              ? moment(reaction.date).format("YYYY-MM-DD")
              : "",
          })
        );
        payload.reactions = feedbackReactions;
        trackUsageValue = `${moment(startDate).format('YYYY/MM/DD')}-${moment(trackPriceDate).format('YYYY/MM/DD')}`
        trackUsageProps.push("Track Price AI News", "PriceChart", SymbolInfo.Symbol, periodicity)
        break;
      }
      default:
        break;
    }
    yield call(aiNewsApi.aiTextFeedbackRequest, payload);
    ConsoleStore.trackUsage("AIFeedbackAdded", trackUsageValue, trackUsageProps);
  } catch (error) {
    console.log(
      `%cError occures in AINewsSaga.js, setAITextReactions ${error}`
    );
  }
}

function* setFutureEventReaction({ event_name, event_type, sentiment, feedback_description, feedback_type, comment }) {
  try {
    const { periodicity, SymbolInfo } = yield select(getDatagraphStates);
    let trackUsageValue = "";
    const trackUsageProps = ["AI", "Future Events", "PriceChart", SymbolInfo.Symbol, periodicity]
    let payload = {
      newsType: AINewsType.FUTURE_EVENTS,
      OSID: SymbolInfo.Osid,
      periodicity: periodicityHelper.convertToPeriodicity(periodicity),
    };
    payload.event_name = event_name;
    payload.event_type = event_type;
    payload.sentiment = sentiment;
    payload.reactions = [{
        feedback_type: feedback_type,
        comments: comment,
        feedback_description: feedback_description,
    }];
    yield call(aiNewsApi.aiTextFeedbackRequest, payload);
    ConsoleStore.trackUsage("AIFeedbackAdded", trackUsageValue, trackUsageProps);
  }
  catch (error) {
    console.log(
      `%cError occures in AINewsSaga.js, setFutureEventReaction ${error}`
    );
  }

}

function findNearestDate(eventDate, dates, periodicity) {
  const eventMoment = moment(eventDate);
  let validWeekDayDate = eventMoment;
  if (eventMoment.day() === 0) { 
    validWeekDayDate = eventMoment.clone().subtract(2, 'days'); 
  } else if (eventMoment.day() === 6) { 
    validWeekDayDate = eventMoment.clone().subtract(1, 'days'); 
  }
  if (!dates || dates.length === 0) return null;
  for (let i = 0; i < dates.length; i++) {
    const currMoment = moment(dates[i]?.Date);
    let diff;
    if (periodicity === GraphType.Monthly) {
      const sameMonth = eventMoment.month() === currMoment.month() &&
        eventMoment.year() === currMoment.year();
      if (sameMonth) {
        return dates[i];
      }
    } else if (periodicity === GraphType.Weekly) {
      diff = Math.abs(validWeekDayDate.diff(currMoment, "days"));
      if (diff < 7) return dates[i];
    } else {
      diff = Math.abs(validWeekDayDate.diff(currMoment, "days"));
      if (diff < 1) return dates[i];
    }
  }
  return null;
}

const matchesMna = (eventName) => {
  if (!eventName) return false;
  const s = String(eventName).toLowerCase();
  return (
    s.includes("merger") ||
    s.includes("acquisition") || 
    s.includes("acquire") ||
    s.includes("m&a") ||   
    s.includes("m & a")           
  );
};

function* prepareAiFutureEventsData(events) {
  const dates = yield select(getTimeLine);
  const { periodicity, nodeWidth } = yield select(getDatagraphStates);
  const padding = 0;
  const { aiSettings } = yield select(getRiPanelAIState);
  const eventsAllowed = aiSettings?.futureEvent.eventsAllowed;
  let filterevents = events.filter(
    (event) =>
      (eventsAllowed.Critical &&
        event.event_type === AI_FUTURE_EVENT_TYPES.CRITICAL) ||
      (eventsAllowed.CompanyNews &&
        event.event_type === AI_FUTURE_EVENT_TYPES.COMPANY_NEWS) ||
      (eventsAllowed.ProductNews &&
        event.event_type === AI_FUTURE_EVENT_TYPES.PRODUCT_NEWS) ||
      (eventsAllowed.Industry &&
        event.event_type === AI_FUTURE_EVENT_TYPES.INDUSTRY) ||
      (eventsAllowed.GovernmentRegulations &&
        event.event_type === AI_FUTURE_EVENT_TYPES.GOVERNMENT_REGULATORY)
  );
  const determineEventDate = (event) => {
    if ((event.event_type === AI_FUTURE_EVENT_TYPES.COMPANY_NEWS && matchesMna(event.event_name)) ||
      event.event_type === AI_FUTURE_EVENT_TYPES.PRODUCT_NEWS) {
      return event.event_date;
    }
    if (!event.start_date || !event.end_date) {
      return event.event_date;
    }
    const start = moment(event.start_date);
    const end = moment(event.end_date);
    const news = event.news_date ? moment(event.news_date) : null;
    if (news && news.isSame(start, "day")) {
      return event.end_date;
    }
    if (start.isSame(end, "day")) {
      return event.start_date;
    }
    if (news) {
      if (start.isAfter(news, "day")) {
        return event.start_date;
      }
      if (start.isBefore(news, "day")) {
        return event.end_date;
      }
    }
    return event.start_date;
  };
  const eventsWithPosition = filterevents.map((event) => {
    const derivedDate = determineEventDate(event);
    let nearestDate = null;
    const today = moment().startOf('day');
    if (derivedDate && moment(derivedDate).isAfter(today)) {
      nearestDate = findNearestDate(derivedDate, dates, periodicity);
    }
    let announedDate = null;
    let endDate = null;
    if ((event.event_type === AI_FUTURE_EVENT_TYPES.COMPANY_NEWS && matchesMna(event.event_name)) ||
      event.event_type === AI_FUTURE_EVENT_TYPES.PRODUCT_NEWS) {
      announedDate = event.news_date ? findNearestDate(event.news_date, dates, periodicity) : null;
      endDate = event.end_date ? findNearestDate(event.end_date, dates, periodicity): null
    }
    return { ...event, xAxis: nearestDate?.xAxis, announedXAxis: announedDate?.xAxis, endDateXAxis: endDate?.xAxis };
  });

  const originalEventsWithPosition = events.map((event) => {
    const derivedDate = determineEventDate(event);
    let nearestDate = null;
    const today = moment().startOf('day');
    if (derivedDate && moment(derivedDate).isAfter(today)) {
      nearestDate = findNearestDate(derivedDate, dates, periodicity);
    }
    let announedDate = null;
    let endDate = null;
    if ((event.event_type === AI_FUTURE_EVENT_TYPES.COMPANY_NEWS && matchesMna(event.event_name)) ||
      event.event_type === AI_FUTURE_EVENT_TYPES.PRODUCT_NEWS) {
      announedDate = event.news_date ? findNearestDate(event.news_date, dates, periodicity): null;
      endDate = event.end_date ? findNearestDate(event.end_date, dates, periodicity): null;
    }
    return { ...event, xAxis: nearestDate?.xAxis, announedXAxis: announedDate?.xAxis, endDateXAxis: endDate?.xAxis };
  });


  filterevents = eventsWithPosition
    .filter((event) => event.xAxis !== undefined && event.xAxis !== null)
    .map((event) => {
      let zone = 2; // Default zone for non-critical
      if (event.event_type === AI_FUTURE_EVENT_TYPES.CRITICAL) {
        zone = 1;
      } else {
        // Assign zone based on sentiment
        switch (String(event.sentiment).toLowerCase()) {
          case "positive":
            zone = 2;
            break;
          case "neutral":
            zone = 3;
            break;
          case "negative":
            zone = 4;
            break;
          default:
            zone = 3;
        }
      }
      const derivedDate = determineEventDate(event);
      const derivedLabels = () => {
        const name = event.event_name?.toUpperCase() ?? "";
        if (AI_FUTURE_EVENT_LABELS[name]) {
          return AI_FUTURE_EVENT_LABELS[name];
        }
        for (const key of Object.keys(AI_FUTURE_EVENT_LABELS)) {
          if (name.includes(key.toUpperCase())) {
            return AI_FUTURE_EVENT_LABELS[key];
          }
        }
        return null;
      }
      return {
        id: event.Id,
        name: event.event_name,
        date: moment(derivedDate).format("YYYY-MM-DD"),
        zone,
        xAxis: event.xAxis + padding * nodeWidth,
        note: event.event_note,
        type: event.event_type,
        sentiment: event.sentiment,
        announedXAxis: event.announedXAxis,
        endDateXAxis: event.endDateXAxis,
        label: event.event_type === AI_FUTURE_EVENT_TYPES.CRITICAL ? derivedLabels() || "NCE" : ""
      };
    });
  const originalEvents = originalEventsWithPosition
    .filter((event) => event.xAxis !== undefined && event.xAxis !== null)
    .map((event) => {
      let zone = 2; // Default zone for non-critical
      if (event.event_type === AI_FUTURE_EVENT_TYPES.CRITICAL) {
        zone = 1;
      } else {
        switch (String(event.sentiment).toLowerCase()) {
          case "positive":
            zone = 2;
            break;
          case "neutral":
            zone = 3;
            break;
          case "negative":
            zone = 4;
            break;
          default:
            zone = 3;
        }
      }
      const derivedDate = determineEventDate(event);
      const derivedLabels = () => {
        const name = event.event_name?.toUpperCase() ?? "";
        if (AI_FUTURE_EVENT_LABELS[name]) {
          return AI_FUTURE_EVENT_LABELS[name];
        }
        for (const key of Object.keys(AI_FUTURE_EVENT_LABELS)) {
          if (name.includes(key.toUpperCase())) {
            return AI_FUTURE_EVENT_LABELS[key];
          }
        }
        return null;
      }
      return {
        id: event.Id,
        name: event.event_name,
        date: moment(derivedDate).format("YYYY-MM-DD"),
        zone,
        xAxis: event.xAxis + padding * nodeWidth,
        note: event.event_note,
        type: event.event_type,
        sentiment: event.sentiment,
        announedXAxis: event.announedXAxis,
        endDateXAxis: event.endDateXAxis,
        label: event.event_type === AI_FUTURE_EVENT_TYPES.CRITICAL ? derivedLabels() || "NCE" : ""
      };
    });
  return { filterevents, originalEvents };
}

function* getAiFutureEvents() {
  try {
    const isFutureEventEntitled = UserInfoUtil.IsUserEntitled(EntitlementType.AI_FUTURE_EVENTS_Entitlement); 
    const { SymbolInfo } = yield select(getDatagraphStates);
    const current_date_iso = moment().format("YYYY-MM-DD");
    const payload = { stock_id: SymbolInfo.Osid, current_date: current_date_iso, symbol: SymbolInfo.Symbol };
    const { applicableInstrumentsFutureEventsAI } = yield select(getAINewsStates);
    const isUSStock = applicableInstrumentsFutureEventsAI.includes(SymbolInfo.SymTypeEnum) ? true : false;
    yield put({
      type: ActionTypes.UPDATE_FUTURE_EVENTS,
      states: {
          futureEvents: [],
          futureEventsOriginal: [],
          futureEventsResponse: null
        }
    });
    if (isUSStock === false || isFutureEventEntitled === false) {
      return;
    }

    const response = yield call(aiNewsApi.getAiFutureEvents, payload);
    if (!response || !response.data) {
      return;
    }

    yield put({
      type: ActionTypes.UPDATE_FUTURE_EVENTS,
      states: {
          futureEventsResponse: response.data
        }
    });

    yield call(updateAiFutureEvents);
  } catch (error) {
    console.log(`%cError occurs in AINewsSaga.js, getAiFutureEvents ${error}`);
  }
}

function* updateAiFutureEvents() {
  try {
    const { futureEventsResponse, applicableInstrumentsFutureEventsAI } = yield select(getAINewsStates);
    const { SymTypeEnum, majorPeriodicity } = yield select(getDatagraphStates);
    if (!futureEventsResponse || futureEventsResponse.length === 0) {
      return;
    }
    const isUSStock = applicableInstrumentsFutureEventsAI.includes(SymTypeEnum) ? true : false;
    const applicablePeriodicity =[GraphType.Daily, GraphType.Weekly, GraphType.Monthly].includes(majorPeriodicity);
    if (isUSStock === false || applicablePeriodicity === false) {
      yield put({
        type: ActionTypes.UPDATE_FUTURE_EVENTS,
        states: {
          futureEvents: [],
          futureEventsOriginal: []
        }
      });
      return;
    }
    const { filterevents, originalEvents } = yield call(prepareAiFutureEventsData, futureEventsResponse);
    yield put({ type: ActionTypes.UPDATE_FUTURE_EVENTS, 
      states: {
          futureEvents: filterevents,
          futureEventsOriginal: originalEvents
       }});
  } catch (error) {
    console.log(`%cError occurs in AINewsSaga.js, updateAiFutureEvents ${error}`);
  }
}

function* closeCurrentEventSource() {
  try {
    baseServiceApi.closeCurrentEventSource();
  } catch (error) {
    console.log(
      `%cError occures in AINewsSaga.js, closeCurrentEventSource ${error}`
    );
  }
}

export function* watchCompetitiveEdge() {
  yield takeLatest(
    ActionTypes.GET_COMPETITIVE_EDGE_REQUEST,
    getCompetitiveEdgeData
  );
}

export function* watchSetAITextReactions() {
  yield takeLatest(ActionTypes.SET_AI_TEXT_REACTION, setAITextReactions);
  yield takeLeading(ActionTypes.SET_FUTURE_EVENT_REACTION, setFutureEventReaction);
}

export function* watchZigzagTrendData() {
  yield takeLatest(
    ActionTypes.GET_ZIGZAG_TREND_DATA_REQUEST,
    getZigzagTrendData
  );
}

export function* watchFinancialBlockMetricsData() {
  yield takeLatest(
    ActionTypes.GET_FINANCIAL_BLOCK_METRICS_DATA_REQUEST,
    getFinancialBlockMetricsData
  );
}

export function* watchTrackPriceAINews() {
  yield takeLatest(
    ActionTypes.GET_TRACK_PRICE_AI_NEWS_REQUEST,
    getTrackPriceAINews
  );
}

export function* watchClosePopup() {
  yield takeLatest(ActionTypes.CLOSE_POPUP, closeCurrentEventSource);
}

export function* watchGetAiFutureEvents() {
  yield takeLatest(PriceChartActionTypes.PRICE_DATA_READY, updateAiFutureEvents);
  yield takeLatest(SettingsConstants.ActionTypes.SYMBOL_CHANGE_EVENT,getAiFutureEvents);
}
